import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { AVAILABILITY_META, formatCell, groupRuns, isEmpty, isNumericKind } from "../format";

// The sheet grid. Unlike src/v2/components/DataTable this one IS sticky (header rows and
// the Order ID column): a 53-column sheet is unreadable once the identifying column
// scrolls away. That needs `border-separate` and opaque cell backgrounds -- the two
// things DataTable gives up to match the legacy tables (see its header comment) -- so
// it is a separate component rather than a DataTable option.

const DELIVERY_TONE = {
    DELIVERED: "bg-elm-50 text-elm-700",
    IN_TRANSIT: "bg-sky-50 text-sky-700",
    OUT_FOR_DELIVERY: "bg-sky-50 text-sky-700",
    READY_TO_SHIP: "bg-indigo-50 text-indigo-700",
    PACKED: "bg-indigo-50 text-indigo-700",
    PACKAGING: "bg-indigo-50 text-indigo-700",
    RETURN: "bg-amber-50 text-amber-800",
    ERROR: "bg-rose-50 text-rose-700",
    CANCELLED: "bg-gray-100 text-gray-600",
};

const WIDE_KEYS = new Set(["notes", "completeAddress", "prxDetail", "substitutionReason", "prxStatus"]);
const STICKY_KEY = "peOrderId";

const GROUP_TONE = {
    Order: "bg-slate-50",
    Patient: "bg-[#f6f8fb]",
    "Billing & Payment": "bg-elm-50",
    Prescription: "bg-[#f7f5fc]",
    Logistics: "bg-[#f2f8fc]",
    TAT: "bg-[#fbf7ef]",
    Remarks: "bg-gray-50",
};

function HeaderCell({ col, sticky }) {
    const meta = AVAILABILITY_META[col.availability] || AVAILABILITY_META.MISSING;
    const tip = [meta.label, col.source ? `Source: ${col.source}` : "Not captured in the database", col.note]
        .filter(Boolean)
        .join("\n");
    return (
        <th
            title={tip}
            className={`sticky top-[30px] z-20 border-b-2 border-gray-200 bg-[white] px-3 py-3 text-left align-bottom font-sans text-[12px] font-bold text-gray-800 ${
                sticky ? "left-0 z-30 shadow-[1px_0_0_#e5e7eb]" : ""
            } ${isNumericKind(col.key) ? "text-right" : ""}`}
        >
            <span className={`inline-flex items-center gap-1.5 ${WIDE_KEYS.has(col.key) ? "" : "whitespace-nowrap"}`}>
                <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${meta.dot}`} aria-hidden="true" />
                <span className="max-w-[180px] leading-tight">{col.label}</span>
            </span>
        </th>
    );
}

HeaderCell.propTypes = {
    col: PropTypes.object.isRequired,
    sticky: PropTypes.bool,
};

HeaderCell.defaultProps = { sticky: false };

function Cell({ col, row }) {
    const value = row[col.key];
    const meta = row._meta || {};

    if (col.availability === "MISSING") {
        return <span className="text-[11px] italic text-gray-300">not captured</span>;
    }
    if (isEmpty(value)) return <span className="text-gray-300">—</span>;

    const text = formatCell(col.key, value);

    switch (col.key) {
        case "peOrderId":
            return (
                <Link to={`/order/edit/${value}`} className="font-bold text-elm-700 hover:underline">
                    #{value}
                </Link>
            );
        case "trackingId":
            return meta.trackingLink ? (
                <a href={meta.trackingLink} target="_blank" rel="noreferrer" className="text-elm-700 hover:underline">
                    {text}
                </a>
            ) : text;
        case "invoiceNo":
            return meta.invoicePrintUrl ? (
                <a href={meta.invoicePrintUrl} target="_blank" rel="noreferrer" className="text-elm-700 hover:underline">
                    {text}
                </a>
            ) : text;
        case "deliveryStatus":
            return (
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${DELIVERY_TONE[value] || "bg-gray-100 text-gray-700"}`}>
                    {String(value).replace(/_/g, " ")}
                </span>
            );
        case "refundPendingCod": {
            const n = Number(value);
            const tone = n > 0.009 ? "text-amber-700" : n < -0.009 ? "text-rose-600" : "text-gray-500";
            const hint = n > 0.009 ? "Pending from customer" : n < -0.009 ? "Excess received / refund due" : "Settled";
            return <span className={tone} title={hint}>{text}</span>;
        }
        case "paymentDate":
            return meta.paymentDateApprox ? (
                <span title="No paidAt on this payment (admin-recorded cash/QR/B2B); showing the record time">{text}*</span>
            ) : text;
        case "dispatchDate":
            return meta.dispatchedBy ? <span title={`Marked in transit by ${meta.dispatchedBy}`}>{text}</span> : text;
        case "caregiverNumber":
            return meta.relationship ? <span title={`Ordered by: ${meta.relationship}`}>{text}</span> : text;
        default:
            if (WIDE_KEYS.has(col.key)) {
                return <span className="block max-h-[3.1em] max-w-[260px] overflow-hidden whitespace-normal leading-[1.5em]" title={text}>{text}</span>;
            }
            return text;
    }
}

Cell.propTypes = {
    col: PropTypes.object.isRequired,
    row: PropTypes.object.isRequired,
};

function SheetTable({ columns: sheetColumns, rows, isFetching }) {
    // The pinned identity column is moved to the front for display only. Everything else
    // keeps the ops sheet's order, and the export (built from the catalog) is untouched.
    const columns = [
        ...sheetColumns.filter((c) => c.key === STICKY_KEY),
        ...sheetColumns.filter((c) => c.key !== STICKY_KEY),
    ];
    const runs = groupRuns(columns);

    return (
        <div className={`relative mt-3 rounded-2xl bg-[white] shadow-soft-xl transition-opacity ${isFetching ? "opacity-60" : ""}`}>
            <div className="max-h-[calc(100vh-300px)] min-h-[320px] overflow-auto rounded-2xl">
                <table className="w-max min-w-full border-separate border-spacing-0 font-sans">
                    <thead>
                        <tr>
                            {runs.map((run) => (
                                <th
                                    key={run.firstKey}
                                    colSpan={run.span}
                                    className={`sticky top-0 z-20 h-[30px] border-b border-gray-200 px-3 text-left font-sans text-[10px] font-bold uppercase tracking-[0.08em] text-gray-500 ${
                                        GROUP_TONE[run.group] || "bg-gray-50"
                                    } ${run.firstKey === columns[0]?.key ? "" : "border-l border-l-white"} ${
                                        run.firstKey === STICKY_KEY && run.span === 1 ? "left-0 z-30" : ""
                                    }`}
                                >
                                    {run.group}
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {columns.map((col) => (
                                <HeaderCell key={col.key} col={col} sticky={col.key === STICKY_KEY} />
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.peOrderId} className="group">
                                {columns.map((col) => {
                                    const sticky = col.key === STICKY_KEY;
                                    const missing = col.availability === "MISSING";
                                    return (
                                        <td
                                            key={col.key}
                                            className={`border-b border-gray-100 px-3 py-2.5 align-top text-[12px] text-gray-900 group-hover:bg-slate-50 ${
                                                sticky ? "sticky left-0 z-10 bg-[white] shadow-[1px_0_0_#e5e7eb]" : missing ? "bg-[repeating-linear-gradient(135deg,#fafafa_0,#fafafa_6px,#f4f4f5_6px,#f4f4f5_7px)]" : "bg-[white]"
                                            } ${isNumericKind(col.key) ? "text-right tabular-nums" : ""} ${WIDE_KEYS.has(col.key) ? "" : "whitespace-nowrap"}`}
                                        >
                                            <Cell col={col} row={row} />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

SheetTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        group: PropTypes.string,
        availability: PropTypes.string,
    })).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    isFetching: PropTypes.bool,
};

SheetTable.defaultProps = { isFetching: false };

export default SheetTable;
